import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useLabelStore = defineStore('label', () => {
    const senseDataUrl = ref()
    const labelData = ref()
    const labelInDatabase = ref()
    const isLabelTap = ref(false)
    // Whether the signed-in user's active company owns the label report or already has access to it
    const isSavedToCompany = ref(false)

    const resetLabel = () => {
        senseDataUrl.value = undefined
        labelData.value = undefined
        labelInDatabase.value = undefined
        isLabelTap.value = false
        isSavedToCompany.value = false
    }

    const labelStatuses = computed(() => {
        return labelData.value?.calculatedSenseData?.statuses
    })

    const labelIsConfigured = computed(() => {
        // Label report is present
        if (labelData.value?.id) return true
        // Label has configured status
        if (labelStatuses.value?.includes('CONFIGURED')) return true
        return false
    })

    const labelIsActive = computed(() => {
        if (!labelStatuses.value?.includes('CONFIGURED') || labelHasStopped.value) return false
        if (labelStatuses.value?.includes('STARTING')) return true
        return false
    })

    const labelHasStopped = computed(() => {
        if (labelStatuses.value?.includes('STOPPED')) return true
        if (labelStatuses.value?.includes('EXPIRED')) return true
        return false
    })

    const labelHasError = computed(() => {
        if (labelStatuses.value?.includes('UNEXPECTED_STOP')) return true
        if (labelStatuses.value?.includes('SPI_ERROR')) return true
        if (labelStatuses.value?.includes('I2C_ERROR')) return true
        return false
    })

    const labelHasViolations = computed(() => {
        if (labelStatuses.value?.includes('TEMPERATURE_TOO_LOW') || labelStatuses.value?.includes('TEMPERATURE_TOO_HIGH')) return true
        if (labelStatuses.value?.includes('HUMIDITY_TOO_LOW') || labelStatuses.value?.includes('HUMIDITY_TOO_HIGH')) return true
        return false
    })

    const statusString = computed(() => {
        if (labelStatuses.value?.includes('EXPIRED')) return 'Label has finished measuring'
        if (labelStatuses.value?.includes('FULL')) return `Label's memory is full`
        if (labelHasError.value && labelStatuses.value?.includes('STOPPED')) return 'Label had an error'
        if (labelStatuses.value?.includes('STOPPED')) return 'Label has stopped measuring'
        if (labelStatuses.value?.includes('LOGGING')) return 'Label is currently measuring'
        if (labelStatuses.value?.includes('STARTING')) return 'Label will start measuring'
        if (labelStatuses.value?.includes('CONFIGURED')) return 'Label is configured'

        return 'Label has not been configured'
    })

    const statusStringShort = computed(() => {
        if (labelStatuses.value?.includes('EXPIRED')) return 'Finished'
        if (labelStatuses.value?.includes('FULL')) return `Memory full`
        if (labelHasError.value && labelStatuses.value?.includes('STOPPED')) return 'Error occurred'
        if (labelStatuses.value?.includes('STOPPED')) return 'Stopped'
        if (labelStatuses.value?.includes('LOGGING')) return 'Measuring'
        if (labelStatuses.value?.includes('STARTING')) return 'Starting'
        if (labelStatuses.value?.includes('CONFIGURED')) return 'Configured'

        return 'Not configured'
    })

    const batteryIsDrained = computed(() => labelStatuses.value?.includes('BOD') ?? false)

    const estimatedBatteryDaysLeft = computed(() => labelData.value?.label?.estimatedBatteryDaysLeft)

    const LOW_BATTERY_DAYS = 10

    // Prefer the estimated days left, but a drained battery always takes precedence over it.
    // Returns undefined when we have nothing meaningful to report, so the battery is left out entirely.
    const batteryLifeString = computed(() => {
        if (batteryIsDrained.value) return 'Drained'
        if (estimatedBatteryDaysLeft.value === undefined) return undefined
        if (estimatedBatteryDaysLeft.value === 0) return 'Low'
        return `${estimatedBatteryDaysLeft.value} days`
    })

    // Anything needing attention is named on the card's label, with an icon badge to flag it
    const ALERT_BADGE = { icon: 'alert' as const }

    // Summary card props for the battery. Same precedence as batteryLifeString.
    const batterySummary = computed(() => {
        if (batteryIsDrained.value) return { label: 'Drained', typeLabel: 'Status', state: 'error' as const, badge: ALERT_BADGE }
        if (estimatedBatteryDaysLeft.value === undefined) return undefined

        const daysLeft = estimatedBatteryDaysLeft.value

        // A spent estimate is named on the label with an icon badge, the same as a drained battery
        if (daysLeft === 0) return { label: 'Low', typeLabel: 'Estimated battery life', state: 'warning' as const, badge: ALERT_BADGE }

        // With days left to count down, the days are the label and the badge says how it is doing
        const isLow = daysLeft <= LOW_BATTERY_DAYS
        return {
            label: `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`,
            typeLabel: 'Estimated battery life',
            state: isLow ? ('warning' as const) : ('success' as const),
            badgeLabel: isLow ? 'Low' : 'Ok'
        }
    })

    // Summary card props for the label status, shown once a label has stopped or its memory is full
    const labelStatusSummary = computed(() => {
        if (!labelHasStopped.value) return undefined
        return {
            label: statusStringShort.value,
            state: labelHasError.value ? ('error' as const) : ('warning' as const),
            badge: ALERT_BADGE
        }
    })

    const labelHasReadAccessToken = computed(() => {
        if (labelData.value?.readAccessToken) return true
        return false
    })

    return {
        senseDataUrl,
        labelData,
        labelInDatabase,
        isLabelTap,
        isSavedToCompany,
        resetLabel,
        labelIsConfigured,
        labelIsActive,
        labelHasStopped,
        labelHasError,
        labelHasViolations,
        statusString,
        statusStringShort,
        batteryIsDrained,
        estimatedBatteryDaysLeft,
        batteryLifeString,
        batterySummary,
        labelStatusSummary,
        labelHasReadAccessToken
    }
})
